import type { TOC } from '@ember/component/template-only';
import { t } from 'ember-intl';
import { not, or } from 'ember-truth-helpers';
import { local } from 'embroider-css-modules';
import GeneralChartsChartUpdater from 'lumen/components/general/charts/chart-updater';
import GeneralCurrencyPicker from 'lumen/components/general/currency-picker';
import GeneralFilterBarFilterBar from 'lumen/components/general/filter-bar/filter-bar';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import HotelBenchmarkInsightBannersBannerTrial from 'lumen/components/hotel/benchmark-insight/banners/banner-trial';
import HotelBenchmarkInsightButtonDesiredCompset from 'lumen/components/hotel/benchmark-insight/button-desired-compset';
import HotelBenchmarkInsightButtonIntercomChecklist from 'lumen/components/hotel/benchmark-insight/button-intercom-checklist';
import HotelBenchmarkInsightCardIntegrationStatus from 'lumen/components/hotel/benchmark-insight/card-integration-status';
import HotelBenchmarkInsightDataSyncStatus from 'lumen/components/hotel/benchmark-insight/data-sync-status';
import HotelBenchmarkInsightDatePickersDatePicker from 'lumen/components/hotel/benchmark-insight/date-pickers/date-picker';
import HotelBenchmarkInsightDesiredCompsetStatus from 'lumen/components/hotel/benchmark-insight/desired-compset-status';
import HotelBenchmarkInsightDropdownsDropdownComparisonSets from 'lumen/components/hotel/benchmark-insight/dropdowns/dropdown-comparison-sets';
import HotelBenchmarkInsightDropdownsDropdownPickup from 'lumen/components/hotel/benchmark-insight/dropdowns/dropdown-pickup';
import HotelBenchmarkInsightDropdownsDropdownReferencePeriod from 'lumen/components/hotel/benchmark-insight/dropdowns/dropdown-reference-period';
import HotelBenchmarkInsightDropdownsDropdownSegment from 'lumen/components/hotel/benchmark-insight/dropdowns/dropdown-segment';
import HotelBenchmarkInsightGridExpandableGridExpandable from 'lumen/components/hotel/benchmark-insight/grid-expandable/grid-expandable';
import HotelBenchmarkInsightSectionsSectionBreakdowns from 'lumen/components/hotel/benchmark-insight/sections/section-breakdowns';
import HotelBenchmarkInsightSectionsSectionChannelSectionChannel from 'lumen/components/hotel/benchmark-insight/sections/section-channel/section-channel';
import HotelBenchmarkInsightSectionsSectionLosSectionLos from 'lumen/components/hotel/benchmark-insight/sections/section-los/section-los';
import HotelBenchmarkInsightSectionsSectionPaceSectionPace from 'lumen/components/hotel/benchmark-insight/sections/section-pace/section-pace';
import HotelBenchmarkInsightSectionsSectionSegmentSectionSegment from 'lumen/components/hotel/benchmark-insight/sections/section-segment/section-segment';
import HotelBenchmarkInsightSectionsSectionStayDateSectionStayDate from 'lumen/components/hotel/benchmark-insight/sections/section-stay-date/section-stay-date';
import HotelBenchmarkInsightTopBanners from 'lumen/components/hotel/benchmark-insight/top-banners';
import type HotelBenchmarkInsightController from 'lumen/controllers/hotel/benchmark-insight';
import type HotelBenchmarkInsightRoute from 'lumen/routes/hotel/benchmark-insight';
import { BREAKDOWNS } from 'lumen/utils/constants/benchmark-insight/beni-constant-values';
import type { ModelFrom } from 'lumen/utils/routes';

import styles from './benchmark-insight.module.css';

interface HotelBenchmarkInsightSignature {
  Args: {
    controller: HotelBenchmarkInsightController;
    model: ModelFrom<HotelBenchmarkInsightRoute>;
  };
}

<template>
  {{! @glint-nocheck: Need to convert controller to *.ts }}
  <GeneralPageHeader>
    <div
      class="content-wrapper flex flex-row items-center justify-between gap-200"
    >
      <div class="flex w-full flex-row items-center gap-200">
        <div class={{styles.dropdown}}>
          <HotelBenchmarkInsightDatePickersDatePicker
            @fromDateMoment={{@controller.fromDateMoment}}
            @rangeChanged={{@controller.rangeChanged}}
            @selectedDateRange={{@controller.selectedDateRange}}
            @toDateMoment={{@controller.toDateMoment}}
          />
        </div>

        <div class={{styles.dropdown}}>
          <HotelBenchmarkInsightDropdownsDropdownSegment
            @onSegmentSelectionChanged={{@controller.onSegmentSelectionChanged}}
            @segmentSelectionOptions={{@controller.segmentSelectionOptions}}
            @selectedSegmentOption={{@controller.selectedSegmentOption}}
          />
        </div>

        <GeneralFilterBarFilterBar
          @activeFilters={{@controller.mappedFilters}}
          @filterBarSegmentationClicked={{@controller.removeFilter}}
          @hideFavourites={{true}}
          @isLoading={{or
            @controller.showSkeletons
            @model.modelTask.isRunning
            @model.dataApiResponse.isRunning
            @model.hierarchy.isRunning
            @model.hierarchyChannel.isRunning
          }}
        />
      </div>

      <div class="flex flex-row items-center gap-400">
        {{#unless @controller.access.hasBenchmarkInsightCompset}}
          {{#if @model.desiredCompset.last.value.compset.length}}
            <HotelBenchmarkInsightDesiredCompsetStatus
              @desiredCompetitors={{@model.desiredCompset.last.value.compset}}
            />
          {{else}}
            <HotelBenchmarkInsightButtonDesiredCompset />
          {{/if}}
        {{/unless}}

        <HotelBenchmarkInsightButtonIntercomChecklist />

        <GeneralCurrencyPicker
          @defaultCurrencyTooltip={{t "revenue_insight.currency.local_in_use"}}
          @fallbackCurrencyCode={{@controller.hotelAndMarketService.hotel.benchmarkInsightCurrencyCode}}
          @onCurrencyChange={{@controller.setCurrency}}
        />
      </div>
    </div>
  </GeneralPageHeader>

  <GeneralPageHeader @isSmall={{true}}>
    <div class="content-wrapper flex flex-row items-center justify-between">
      <div class="flex flex-row items-center gap-200">
        {{t "benchmark_insight.header.compare_to"}}

        <div class="{{styles.dropdown}} flex flex-row gap-200">
          <HotelBenchmarkInsightDropdownsDropdownComparisonSets
            @primaryComparisonSet={{@controller.primaryDataSetWithFallback}}
            @secondaryComparisonSet={{@controller.secondaryDataSetWithFallback}}
            @setPrimaryComparisonSet={{@controller.setPrimaryDataSet}}
            @setSecondaryComparisonSet={{@controller.setSecondaryDataSet}}
          />
        </div>

        {{#if @controller.access.hasBenchmarkInsightReferencePeriodData}}
          <div class={{local styles "dropdown" "dropdown-reference-period"}}>
            <HotelBenchmarkInsightDropdownsDropdownReferencePeriod
              @onChangeReferencePeriod={{@controller.onChangeReferencePeriod}}
              @referencePeriod={{@controller.referencePeriod}}
              @selectedDateRange={{@controller.selectedDateRange}}
            />
          </div>
        {{/if}}

        {{#if @controller.access.hasBenchmarkInsightPickupData}}
          <div class={{styles.dropdown}}>
            <HotelBenchmarkInsightDropdownsDropdownPickup
              @onChangePickup={{@controller.onChangePickup}}
              @pickup={{@controller.pickup}}
              @selectedDateRange={{@controller.selectedDateRange}}
            />
          </div>
        {{/if}}
      </div>

      <div class="flex flex-row items-center gap-200">
        {{#unless
          (or
            @controller.showSkeletons
            @model.modelTask.isRunning
            @model.dataApiResponse.isRunning
            @model.hierarchy.isRunning
            @model.hierarchyChannel.isRunning
          )
        }}
          <HotelBenchmarkInsightDataSyncStatus
            @comparisonSets={{@model.dataApiResponse.last.value.meta.comparisonSets}}
            @extractDate={{@model.dataApiResponse.last.value.meta.extractDate}}
            @extractDateDebugging={{@controller.extractDate}}
          />
        {{/unless}}
      </div>
    </div>
  </GeneralPageHeader>

  <GeneralPageMain>
    {{! Outlet for the market-composition route }}
    {{outlet}}

    <HotelBenchmarkInsightTopBanners
      @meta={{@model.dataApiResponse.last.value.meta}}
      @primaryComparisonSetType={{@controller.primaryDataSetWithFallback}}
      @secondaryComparisonSetType={{@controller.secondaryDataSetWithFallback}}
    />

    <div class="benchmark-insight content-wrapper main-content">
      {{! Don't show the integration status in the upsell child route }}
      {{#if
        @controller.benchmarkInsightIntegrationStatus.showIntegrationStatus
      }}
        {{! Nested if because and helper doesn't narrow the integrationStatus type }}
        {{#if @controller.benchmarkInsightIntegrationStatus.integrationStatus}}
          {{#unless @controller.hasDismissedCardIntegrationStatus}}
            <section class="mb-800">
              <HotelBenchmarkInsightCardIntegrationStatus
                @dismissCardIntegrationStatus={{@controller.dismissCardIntegrationStatus}}
                @integrationStatus={{@controller.benchmarkInsightIntegrationStatus.integrationStatus}}
              />
            </section>
          {{/unless}}
        {{/if}}
      {{/if}}

      {{#if @controller.showBannerTrial}}
        <section class="mb-800">
          <HotelBenchmarkInsightBannersBannerTrial
            @closeBanner={{@controller.closeBannerTrial}}
          />
        </section>
      {{/if}}

      <section class="mb-800">
        <HotelBenchmarkInsightSectionsSectionStayDateSectionStayDate
          @changeSelectedAxisOption={{@controller.changeSelectedAxisOption}}
          @changeSelectedKpi={{@controller.changeSelectedKpiHeroChart}}
          @dateAggregation={{@controller.dateAggregation}}
          @dateAggregationOptions={{@controller.dateAggregationOptions}}
          @dayBreakdownsByComparisonSet={{@model.dataApiResponse.last.value.filteredDaySegmentations}}
          @meta={{@model.dataApiResponse.last.value.meta}}
          @pickupTranslation={{@controller.pickupTranslation}}
          @primaryComparisonSetType={{@controller.primaryDataSetWithFallback}}
          @rangeChanged={{@controller.rangeChanged}}
          @referencePeriod={{@controller.referencePeriod}}
          @secondaryComparisonSetType={{@controller.secondaryDataSetWithFallback}}
          @selectedAxisOption={{@controller.selectedAxisOption}}
          @selectedDateAggregation={{@controller.selectedDateAggregation}}
          @selectedKpi={{@controller.selectedKpiHeroChart}}
          @selectedRange={{@controller.selectedDateRange}}
          @setDateAggregation={{@controller.setDateAggregation}}
          @setStayDateSectionView={{@controller.setStayDateSectionView}}
          @showSkeleton={{or
            @controller.showSkeletons
            @model.modelTask.isRunning
            @model.dataApiResponse.isRunning
          }}
          @showSkeletonAnimation={{not @controller.showSkeletons}}
          @stayDateSectionView={{@controller.stayDateSectionView}}
          @weekBreakdownsByComparisonSet={{@model.dataApiResponse.last.value.filteredWeekSegmentations}}
        />
      </section>

      {{#if @controller.showSectionPace}}
        <section class="mb-800">
          <HotelBenchmarkInsightSectionsSectionPaceSectionPace
            @breakdownsByComparisonSet={{@model.paceApiResponse.last.value.filteredDaySegmentations}}
            @meta={{@model.paceApiResponse.last.value.meta}}
            @primaryComparisonSetType={{@controller.primaryDataSetWithFallback}}
            @referencePeriod={{@controller.referencePeriod}}
            @secondaryComparisonSetType={{@controller.secondaryDataSetWithFallback}}
            @showSkeleton={{or
              @controller.showSkeletons
              @model.modelTask.isRunning
              @model.paceApiResponse.isRunning
            }}
            @showSkeletonAnimation={{not @controller.showSkeletons}}
          />
        </section>
      {{/if}}

      <section
        class={{if
          (or
            @controller.access.hasBenchmarkInsightLengthOfStay
            @controller.access.hasBenchmarkInsightChannel
          )
          "mb-800"
        }}
      >
        <div class="section-title">
          <div class="flex items-center justify-between">
            <h2 class="text-neutral-emphasis mr-200 flex items-center">
              {{t "benchmark_insight.breakdown"}}
            </h2>
          </div>
        </div>

        <HotelBenchmarkInsightSectionsSectionSegmentSectionSegment
          @breakdownsByComparisonSet={{@model.dataApiResponse.last.value.filteredSegmentSegmentations}}
          @dateAggregation={{@controller.dateAggregation}}
          @meta={{@model.dataApiResponse.last.value.meta}}
          @pickupTranslation={{@controller.pickupTranslation}}
          @primaryComparisonSetType={{@controller.primaryDataSetWithFallback}}
          @rangeChanged={{@controller.rangeChanged}}
          @referencePeriod={{@controller.referencePeriod}}
          @rootCategory={{@controller.rootCategorySegment}}
          @secondaryComparisonSetType={{@controller.secondaryDataSetWithFallback}}
          @segmentItems={{@controller.segmentItems}}
          @selectedRange={{@controller.selectedDateRange}}
          @selectedSegment={{@controller.segment}}
          @setDateAggregation={{@controller.setDateAggregation}}
          @showSkeleton={{or
            @controller.showSkeletons
            @model.modelTask.isRunning
            @model.dataApiResponse.isRunning
            @model.hierarchy.isRunning
          }}
          @showSkeletonAnimation={{not @controller.showSkeletons}}
        />
      </section>

      {{#if @controller.benchmarkInsightFeatures.showTrialCtas}}
        <HotelBenchmarkInsightSectionsSectionBreakdowns />
      {{else}}
        <HotelBenchmarkInsightGridExpandableGridExpandable
          @expandedIndices={{@controller.expandedBreakdownIndices}}
          @toggleExpandedIndex={{@controller.toggleExpandedBreakdownIndex}}
          as |Grid|
        >
          {{#if @controller.access.hasBenchmarkInsightChannel}}
            <GeneralChartsChartUpdater as |chartUpdater|>
              <Grid.Item
                @index={{0}}
                @reflowChart={{chartUpdater.reflowChart}}
                as |GridExpandableItem|
              >
                <HotelBenchmarkInsightSectionsSectionChannelSectionChannel
                  @breakdownType={{BREAKDOWNS.CHANNEL}}
                  @breakdownsByComparisonSet={{@model.dataApiResponse.last.value.filteredChannelSegmentations}}
                  @changeSelectedOccupancyView={{@controller.changeSelectedChannelOccupancyView}}
                  @channel={{@controller.channel}}
                  @hierarchyChannelTask={{@model.hierarchyChannel}}
                  @isExpanded={{GridExpandableItem.isExpanded}}
                  @meta={{@model.dataApiResponse.last.value.meta}}
                  @onUpdateChartReference={{chartUpdater.onUpdateChartReference}}
                  @pickupTranslation={{@controller.pickupTranslation}}
                  @primaryComparisonSetType={{@controller.primaryDataSetWithFallback}}
                  @referencePeriod={{@controller.referencePeriod}}
                  @secondaryComparisonSetType={{@controller.secondaryDataSetWithFallback}}
                  @selectedKpiChannel={{@controller.selectedKpiChannel}}
                  @selectedOccupancyView={{@controller.selectedChannelOccupancyView}}
                  @selectedRange={{@controller.selectedDateRange}}
                  @setChannel={{@controller.setChannel}}
                  @setSelectedKpiChannel={{@controller.setSelectedKpiChannel}}
                  @showSkeleton={{or
                    @controller.showSkeletons
                    @model.modelTask.isRunning
                    @model.dataApiResponse.isRunning
                    @model.hierarchyChannel.isRunning
                  }}
                  @showSkeletonAnimation={{not @controller.showSkeletons}}
                >
                  <:expandButton>
                    <GridExpandableItem.ExpandButton />
                  </:expandButton>
                </HotelBenchmarkInsightSectionsSectionChannelSectionChannel>
              </Grid.Item>
            </GeneralChartsChartUpdater>
          {{/if}}

          {{#if @controller.access.hasBenchmarkInsightLengthOfStay}}
            <GeneralChartsChartUpdater as |chartUpdater|>
              <Grid.Item
                @index={{1}}
                @reflowChart={{chartUpdater.reflowChart}}
                as |GridExpandableItem|
              >
                <HotelBenchmarkInsightSectionsSectionLosSectionLos
                  @breakdownType={{BREAKDOWNS.LENGTH_OF_STAY}}
                  @breakdownsByComparisonSet={{@model.dataApiResponse.last.value.filteredLosSegmentations}}
                  @changeSelectedLosSortOption={{@controller.changeSelectedLosSortOption}}
                  @changeSelectedOccupancyView={{@controller.changeSelectedLosOccupancyView}}
                  @isExpanded={{GridExpandableItem.isExpanded}}
                  @los={{@controller.los}}
                  @meta={{@model.dataApiResponse.last.value.meta}}
                  @onUpdateChartReference={{chartUpdater.onUpdateChartReference}}
                  @parsedFilters={{@controller.parsedFilters}}
                  @pickupTranslation={{@controller.pickupTranslation}}
                  @primaryComparisonSetType={{@controller.primaryDataSetWithFallback}}
                  @referencePeriod={{@controller.referencePeriod}}
                  @secondaryComparisonSetType={{@controller.secondaryDataSetWithFallback}}
                  @selectedKpiLosChart={{@controller.selectedKpiLosChart}}
                  @selectedLosSortOption={{@controller.selectedLosSortOption}}
                  @selectedOccupancyView={{@controller.selectedLosOccupancyView}}
                  @selectedRange={{@controller.selectedDateRange}}
                  @setLos={{@controller.setLos}}
                  @setSelectedKpiLosChart={{@controller.setSelectedKpiLosChart}}
                  @showSkeleton={{or
                    @controller.showSkeletons
                    @model.modelTask.isRunning
                    @model.dataApiResponse.isRunning
                  }}
                  @showSkeletonAnimation={{not @controller.showSkeletons}}
                  @varianceTranslation={{@controller.varianceTranslation}}
                >
                  <:expandButton>
                    <GridExpandableItem.ExpandButton />
                  </:expandButton>
                </HotelBenchmarkInsightSectionsSectionLosSectionLos>
              </Grid.Item>
            </GeneralChartsChartUpdater>
          {{/if}}
        </HotelBenchmarkInsightGridExpandableGridExpandable>
      {{/if}}
    </div>
  </GeneralPageMain>
</template> satisfies TOC<HotelBenchmarkInsightSignature>;
